import { Typography } from "@mui/material";
import React from "react";
import { FileRejection } from "react-dropzone";

import { Button } from "@/components/elements/Button";
import { DropZone } from "@/components/elements/DropZone";
import { SCAN_ACCEPT_EXT_LABEL, SCAN_ACCEPT_MIME_TYPES } from "@/features/talent/constants/scan-accept";
import { black, orange } from "@/theme/colors";
import { FCC } from "@/types/common";
import { TExtendFile } from "@/types/file";
import { convertMbToByte } from "@/utils/file";
import { toast } from "@/utils/toast";

type TDropZoneScan = {
  handleChangeFiles: (files: TExtendFile | TExtendFile[] | null) => Promise<void>;
};

const MAX_SIZE_BYTES = convertMbToByte(10);

/**
 * Unified scan dropzone for the talent AI-import modal (IROHANA_MATCH_DEV-280).
 * Replaces the old DropZoneCard / DropZoneResume split — the BE now classifies
 * card vs resume vs note from a single batch, so the FE no longer needs the
 * fileType selector. Rules mirror the BE FilesInterceptor in
 * scan-image-upload.constants.ts (defense in depth).
 */
export const DropZoneScan: FCC<TDropZoneScan> = ({ handleChangeFiles }) => {
  // Override DropZone's default reject toast so type-reject messages match the
  // app-wide format ("以下のファイル形式はアップロードできません。" header + bullet
  // list of file names + accepted extensions). Size rejects keep the existing
  // DropZone toast — we re-emit it here because DropZone short-circuits its
  // own toast once handleFileRejections is provided.
  const handleFileRejections = (fileRejections: FileRejection[]) => {
    const sizeRejects = fileRejections.filter((r) => r.errors.some((e) => e.code === "file-too-large"));
    const typeRejects = fileRejections.filter((r) => !r.errors.some((e) => e.code === "file-too-large"));

    if (typeRejects.length > 0) {
      const fileLines = typeRejects.map((r) => `◦${r.file.name}`).join("\n");
      toast.error(`以下のファイル形式はアップロードできません。\n${fileLines}\n（アップロード可能なファイル形式は${SCAN_ACCEPT_EXT_LABEL}です。）`);
      return;
    }

    if (sizeRejects.length > 0) {
      const errorSize = `以下のファイルはサイズ上限（${MAX_SIZE_BYTES / 1000}KB）を超えているため、アップロードできませんでした：\n${sizeRejects
        .map((r) => `◦${r.file.name}`)
        .join("\n")}`;
      toast.error(errorSize);
    }
  };

  return (
    <DropZone
      options={{ multiple: true, noClick: true }}
      onChange={handleChangeFiles}
      rules={{
        maxFile: 4,
        maxSize: MAX_SIZE_BYTES,
        acceptMimeTypes: SCAN_ACCEPT_MIME_TYPES,
      }}
      handleFileRejections={handleFileRejections}
      isShowErr={false}
    >
      <Typography variant="body14Light" whiteSpace="pre-wrap" textAlign="center">
        アップロードするファイルをドラッグ&ドロップ
        <br />
        <Typography variant="body14" color={black[90]}>
          （ファイル形式：{SCAN_ACCEPT_EXT_LABEL}に対応）
        </Typography>
      </Typography>

      <Typography variant="body14Light" width={920} textAlign="center">
        または
      </Typography>
      <Button type="button" variant="outline">
        ファイルを選択する
      </Button>

      <Typography variant="body14Light" color={orange[80]} textAlign="center">
        PDF以外のファイルはプレビューに対応していません。
      </Typography>
    </DropZone>
  );
};
